import {
    CreateMessageBody,
    GetMessageListActionParams,
    GetMessageParams,
    ReactionOperation,
    UploadFileType,
    User,
} from '@Techedge/digitalportal-shared'
import { ActivitiesFiltersProps, ActivityProps } from './activities'
import { TabBar } from './tab-bar'

export interface NewsUpdateTotalReactionsActionPayload {
    newsId: string
    type: NewsReactionType
    commentId?: string
}
export interface NewsAddCommentActionPayload {
    comment: NewsComment
    newsId: string
    commentId?: string
}

export enum NewsTabBarId {
    ALL = 'all',
    LAST_WEEK = 'last-week',
    CURRENT_WEEK = 'current-week',
    CURRENT_MONTH = 'current-month',
}

export enum NewsType {
    COMMUNICATION = 'communication',
    STORE_VISIT_TOOL = 'store-visit-tool',
    PICTURE_MANAGMENT = 'picture-managment',
    SURVEY = 'survey',
    IN_PROGRESS = 'inProgress',
    TO_BE_RELEASED = 'toBeReleased',
    NEWS = 'news',
}

export enum NewsReactionType {
    LIKE = 'thumb-up',
    HEART = 'love',
    FACE = 'laugh',
    FACE_HEART = 'heartEyes',
    THUMB = 'thumbUp',
}

export interface NewsProps {
    newsList: News[]
    loading: boolean
    filters: ActivitiesFiltersProps
    showContent: boolean
    hideSidebar: boolean
    activities: ActivityProps[]
    tabBarConfiguration: TabBar[]
    activeTab: NewsTabBarId
    getNews: (customFilters?: ActivitiesFiltersProps) => void
    setFilters: (filters: ActivitiesFiltersProps) => void
    setActiveTab: (activeTab: NewsTabBarId) => void
    orderRandomNews: () => void
    tabBarHandler: (tabId: NewsTabBarId) => void
}

export interface NewsItemCtaProps {
    title: string
    icon: string
    rightIcon?: string
    disabled?: boolean
    onClick: () => void
}

export interface NewAuthor {
    name: string
    lastname: string
    alias?: string
    avatar?: string // base64 ?
    nickname?: string
}

export interface NewsBase {
    id: string
    creationDate: string
    type: NewsType
    typeKey: string
    title: string
    content: string
    hashtagList: string[]
    imageList: string[]
    store_ids?: string[]
    totalComments: number
    reactions?: NewsReaction
    commentsList?: NewsComment[]
    enableCommentsAndReactions?: boolean
}

export interface NewsReaction {
    count: number
    userReaction?: NewsReactionType
    types: NewsReactionType[]
}

export interface News extends NewsBase {
    author: NewAuthor
    loadingReactions: boolean
}

export interface NewsItemProps {
    news: News
    user: User
    commentsList: NewsComment[]
    loadingMore?: boolean
    reactions: NewsReaction
    isDetailsMode?: boolean
    setNewsDetails?: (newsDetails: News) => void
    openActivity?: (a: any) => void
    createMessage: (body: CreateMessageBody, topicId: string, nodeIds: string[]) => void
    getMessageList: (args: GetMessageListActionParams) => void
    editMessage: (body: CreateMessageBody, id: string, nodeIds: string[], parentId: string) => void
    uploadFile: (
        file,
        type: UploadFileType,
        parentId: string,
        topicId: string,
        nodeIds: string[]
    ) => void
    removeFile: (fileId: string, parentId: string, topicId: string, nodeIds: string[]) => void
    deleteMessage: (id: string, body: GetMessageParams, nodeIds: string[], parentId: string) => void
    reactionMessage: (
        body: Partial<CreateMessageBody>,
        id: string,
        topicId: string,
        nodeIds: string[],
        parentId: string,
        reaction: NewsReactionType
    ) => void
    loadMore: (
        messageId: string,
        partialBody: GetMessageListActionParams,
        topicId: string,
        nodeIds: string[]
    ) => void
    initTopic: (topicId: string) => void
    resetTopic: (topicId: string) => void
    topicMap: Record<string, any>
    setShowSidebar: (show: boolean) => void
    reactToNews: (id: string, reaction: NewsReactionType) => void
    loadingReactions: { [newsId: string]: boolean }
    copyLinkToClipboard: (message: string) => void
}

export interface NewsItemCtaContainerProps extends NewsBase {
    onComment: () => void
    updateTotalReactions?: (payload: NewsUpdateTotalReactionsActionPayload) => void
    showComments?: boolean
}

export interface NewsComment {
    id: string
    author: NewAuthor
    comment: string
    creationDate: string
    replyList?: NewsComment[]
    reactions?: NewsReaction
}

export interface NewsItemCommentProps {
    newsId: string
    userName: string
    userLastname: string
    userNickname?: string
    avatarImage: string
    comment: string
    creationDate: string
    reactions?: NewsReaction
    nickname: string
    commentId?: string
    onReply?: (commentId: string) => void
    updateTotalReactions?: (payload: NewsUpdateTotalReactionsActionPayload) => void
}

export interface NewsItemContentProps extends Partial<News> {
    showAllContent: boolean
    onReadMore: () => void
    openActivity: (a: any) => void
}

export interface NewsItemHeaderProps {
    isDetailsMode: boolean
    userName: string
    userLastname: string
    userNickname?: string
    avatarImage: string
    creationDate: string
    type: NewsType
    typeKey: string
    onBack: () => void
    openActivity: (a: any) => void
}

export interface NewsItemCommentContainerProps {
    newsId: string
    commentsList: NewsComment[]
    authorName: string
    authorLastname: string
    authorAvatar: string
    authorNickname: string
    isDetailsMode?: boolean
}

export interface NewsItemAddCommentProps {
    newsId: string
    commentId?: string
    userName: string
    userLastname: string
    userNickname: string
    avatarImage: string
    addComment?: (comment: NewsAddCommentActionPayload) => void
}

export interface ReactionBody {
    reaction: NewsReactionType
    reactionOperation: ReactionOperation
}
